/**
 * "Does this deployment have passwords?" — for React components (US-009).
 *
 * <h2>Why a hook and not `resolveAuthProviderName()` at the call site</h2>
 *
 * `VITE_AUTH_PROVIDER` is a BUILD-time constant. The backend's answer is a
 * RUNTIME one, and US-006 makes the rollback a backend config flip — a served
 * bundle can therefore outlive the provider it was built for. So the seam
 * used by password-shaped UI is: start from the build-time flag (available
 * synchronously, so the first paint is already right in the overwhelmingly
 * common case where the two agree), then adopt `auth_provider` from
 * `GET /api/config/public` when it lands.
 *
 * The env flag is read on every mount rather than memoised at module load, so
 * a test can drive both bindings in one file with `vi.stubEnv`.
 *
 * <h2>Why each caller fetches for itself</h2>
 *
 * There is no public-config context in this SPA; `loadStripeSingleton`,
 * `ClaudeEditMenu`, `AddLandingPageHub` and `OnboardingWizardPage` each call
 * `fetchPublicConfig()` independently already. Following that pattern keeps
 * this change local — at most two of these components are mounted at once, the
 * endpoint is unauthenticated and cheap, and introducing a module-level cache
 * would add cross-test bleed for no user-visible gain.
 *
 * A failed fetch is swallowed on purpose: the build-time flag is a usable
 * answer, and a dead config endpoint must not blank out an invite page.
 *
 * <h2>UI SHAPE ONLY — AND SINCE US-011 THE ADAPTER IS RUNTIME TOO</h2>
 *
 * <p>This hook decides which password-shaped UI renders, and only that. It
 * still does not change which auth adapter runs — but the reason has changed,
 * and the change matters enough to state rather than delete.
 *
 * <p><b>Until US-011</b>, `getAuthAdapter()` read the BUILD-TIME
 * `VITE_AUTH_PROVIDER` flag, so every sign-in, token fetch, silent renewal and
 * logout went to the provider THE BUNDLE WAS BUILT FOR no matter what this hook
 * answered. A component that branched on this hook and also called into the
 * auth seam held two answers that could disagree, and both directions of skew
 * did damage.
 *
 * <p><b>Now</b> the adapter resolves from `login_provider` on the same
 * `GET /api/config/public` response this hook reads, adopted by
 * `loadRuntimeAuthConfig()` — which `AuthContext` FIRES before the session
 * bootstrap dispatches but awaits only when completing a Scalekit callback, so
 * on a cold load the adopt can land after the first dispatch and `boot()`
 * re-dispatches onto the adapter the answer names. Both answers therefore come
 * from the same fetch of the same
 * document, so the skew window is one request rather than one deploy — and the
 * two fields still answer DIFFERENT questions, deliberately:
 *
 * <ul>
 *   <li>`auth_provider` — which provider ADMINISTERS users, i.e. whether this
 *       deployment has passwords. What this hook returns.</li>
 *   <li>`login_provider` — which adapter the browser signs in through. What
 *       the seam returns.</li>
 * </ul>
 *
 * <p>They are separate switches on purpose (`DASHBOARD_SCALEKIT_LOGIN_ENABLED`
 * vs `IDENTITY_ADMIN_PROVIDER`), so a deployment can administer users in
 * Scalekit before it sends browsers there. In that window this hook answers
 * scalekit — correctly, there are no passwords — while the seam still answers
 * keycloak, and `AcceptInvitePage`'s `adapterSkew` guard is what keeps invite
 * redemption from completing against the wrong subject. Keep it.
 *
 * <h3>The ordering requirement, reduced but not gone</h3>
 *
 * <blockquote>
 *   The backend that serves `login_provider` on `GET /api/config/public` must be
 *   live BEFORE any deployment advertises Scalekit login.
 * </blockquote>
 *
 * <p>An older backend omits the field; the SPA cannot adopt what it is not
 * sent, falls back to the build-time flag, and then to keycloak. On a bundle
 * built with `VITE_AUTH_PROVIDER=scalekit` that fallback is now the SAFE
 * outcome rather than the lockout it used to be — Keycloak keeps working
 * because US-007 leaves its dashboard tenant accepting tokens — but the deploy
 * order is still the one to use.
 *
 * <p>A FAILED fetch keeps the build-time answer at both layers, which is the
 * same reasoning once more: each is right exactly when the deployment matches
 * the bundle it is serving, and neither can produce "nobody can sign in".
 *
 * <p>The fail-safe DIRECTION is not the bug and must not be flipped. Defaulting
 * an absent field to scalekit would hide the password form from every user of
 * an actual Keycloak deployment — locking out everyone instead of pausing one
 * invitee — and would defeat US-006's rollback, which is a backend config flip
 * that this field is how the SPA learns about.
 */

import { useEffect, useState } from 'react';

import { fetchPublicConfig, loadRuntimeAuthConfig } from '../api/publicConfig';
import {
  resolveAuthProviderName,
  resolveBuildTimeAuthProviderName,
  type AuthProviderName,
} from './authProvider';

export const useAuthProviderName = (): AuthProviderName => {
  // Lazy initialiser: runs on mount, not on every render, and reads
  // `import.meta.env` at that moment.
  //
  // The BUILD-TIME flag specifically, not `resolveAuthProviderName()`. That one
  // now answers `login_provider`, a DIFFERENT question, and seeding this hook
  // from it would flash the passwordless branch on a deployment that has
  // advertised Scalekit login while still administering users in Keycloak —
  // hiding the password form from people who have one, for one render. The
  // build-time flag is the same seed this hook has always used.
  const [provider, setProvider] = useState<AuthProviderName>(resolveBuildTimeAuthProviderName);

  useEffect(() => {
    let cancelled = false;
    void fetchPublicConfig()
      .then((config) => {
        if (!cancelled) setProvider(config.authProvider);
      })
      .catch(() => {
        // Keep the build-time fallback. See the module comment.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return provider;
};

/**
 * Which adapter the browser signs in through.
 *
 * Unlike {@link useAuthProviderName}, this reads `login_provider` and adopts
 * the same runtime snapshot used by the provider-neutral auth seam. Keeping
 * those two operations together matters on split-provider deployments: the
 * invite page must not render the Scalekit entry point while
 * `resolveAuthProviderName()` still dispatches its click to Keycloak.
 *
 * The synchronous seed is still usable when the config endpoint fails or
 * hangs. `loadRuntimeAuthConfig()` swallows failures by contract, and a hung
 * request leaves the build-time/runtime answer in place rather than blanking a
 * public account route.
 */
export const useLoginProviderName = (): AuthProviderName => {
  const [provider, setProvider] = useState<AuthProviderName>(resolveAuthProviderName);

  useEffect(() => {
    let cancelled = false;
    void loadRuntimeAuthConfig().then(() => {
      if (!cancelled) setProvider(resolveAuthProviderName());
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return provider;
};
